import { drawArmorIcon, drawBubble, drawFood, drawHeart } from "./icons";

/**
 * Survival status readout: Minecraft-style hearts, armor bar, air bubbles, and hunger bar,
 * plus a red damage flash. Elements are painted as pixel canvases so icons read
 * exactly like the classic sprites.
 */
export class Vitals {
  private readonly statusEl: HTMLElement;
  private readonly statusTopEl: HTMLElement;
  private readonly armorEl: HTMLElement;
  private readonly heartsEl: HTMLElement;
  private readonly airEl: HTMLElement;
  private readonly hungerEl: HTMLElement;
  private readonly flashEl: HTMLElement;

  private readonly armorIcons: HTMLCanvasElement[] = [];
  private readonly hearts: HTMLCanvasElement[] = [];
  private readonly bubbles: HTMLCanvasElement[] = [];
  private readonly foodIcons: HTMLCanvasElement[] = [];

  private lastArmor = -1;
  private lastHealth = -1;
  private lastAir = -1;
  private lastFood = -1;
  private lastSaturation = -1;
  private lastShowAir = false;
  private lastFlash = -1;

  constructor(root: HTMLElement) {
    this.statusEl = must(root.querySelector<HTMLElement>("#status"));
    this.statusTopEl = must(root.querySelector<HTMLElement>(".status-top"));
    this.armorEl = must(root.querySelector<HTMLElement>("#armor"));
    this.heartsEl = must(root.querySelector<HTMLElement>("#hearts"));
    this.airEl = must(root.querySelector<HTMLElement>("#air"));
    this.hungerEl = must(root.querySelector<HTMLElement>("#hunger"));
    this.flashEl = must(root.querySelector<HTMLElement>("#damage-flash"));

    this.armorEl.style.display = "none";
    this.airEl.style.display = "none";
    this.statusTopEl.style.display = "none";

    // The HUD is rebuilt for every world; start from a clean slate.
    this.armorEl.textContent = "";
    this.heartsEl.textContent = "";
    this.airEl.textContent = "";
    this.hungerEl.textContent = "";

    for (let i = 0; i < 10; i++) {
      const armorIcon = document.createElement("canvas");
      armorIcon.className = "armor-icon";
      drawArmorIcon(armorIcon, 0);
      this.armorEl.appendChild(armorIcon);
      this.armorIcons.push(armorIcon);

      const heart = document.createElement("canvas");
      heart.className = "heart";
      drawHeart(heart, 1);
      this.heartsEl.appendChild(heart);
      this.hearts.push(heart);

      const bubble = document.createElement("canvas");
      bubble.className = "bubble";
      drawBubble(bubble, true);
      this.airEl.appendChild(bubble);
      this.bubbles.push(bubble);

      const foodIcon = document.createElement("canvas");
      foodIcon.className = "food-icon";
      drawFood(foodIcon, 1);
      this.hungerEl.appendChild(foodIcon);
      this.foodIcons.push(foodIcon);
    }
  }

  private updateStatusTopVisibility(): void {
    const show =
      (this.armorEl.style.display !== "none" && this.lastArmor > 0) ||
      (this.airEl.style.display !== "none" && this.lastShowAir);
    this.statusTopEl.style.display = show ? "flex" : "none";
  }

  setVisible(visible: boolean): void {
    this.statusEl.style.display = visible ? "flex" : "none";
    this.armorEl.style.display = visible && this.lastArmor > 0 ? "flex" : "none";
    this.heartsEl.style.display = visible ? "flex" : "none";
    this.hungerEl.style.display = visible ? "flex" : "none";
    this.airEl.style.display = visible && this.lastShowAir ? "flex" : "none";
    this.updateStatusTopVisibility();
    if (!visible) this.setDamageFlash(0);
  }

  /**
   * Set armor defense points (0..20).
   * 1 point = half chestplate icon, 2 points = full chestplate icon (10 icons max = 20 points).
   */
  setArmor(points: number): void {
    const clamped = Math.max(0, Math.min(20, Math.round(points)));
    if (clamped === this.lastArmor) return;
    this.lastArmor = clamped;
    this.armorEl.style.display =
      clamped > 0 && this.heartsEl.style.display !== "none" ? "flex" : "none";
    this.updateStatusTopVisibility();

    for (let i = 0; i < 10; i++) {
      const threshold = (i + 1) * 2;
      const state: 0 | 0.5 | 1 =
        clamped >= threshold ? 1 : clamped >= threshold - 1 ? 0.5 : 0;
      drawArmorIcon(this.armorIcons[i], state);
    }
  }

  /**
   * Set hunger level (0..20) and saturation level.
   * 1 point = half shank icon, 2 points = full shank icon (10 icons max = 20 points).
   * When saturation reaches 0.0, the shanks jitter subtly, mirroring Minecraft 1.8.8.
   */
  setFood(points: number, saturation: number): void {
    const clamped = Math.max(0, Math.min(20, Math.round(points)));
    const lowSat = saturation <= 0 && clamped > 0;
    if (lowSat) {
      this.hungerEl.classList.add("hunger-shake");
    } else {
      this.hungerEl.classList.remove("hunger-shake");
    }

    if (clamped === this.lastFood && Math.abs(saturation - this.lastSaturation) < 0.01) return;
    this.lastFood = clamped;
    this.lastSaturation = saturation;

    for (let i = 0; i < 10; i++) {
      const threshold = (i + 1) * 2;
      const state: 0 | 0.5 | 1 =
        clamped >= threshold ? 1 : clamped >= threshold - 1 ? 0.5 : 0;
      drawFood(this.foodIcons[i], state);
    }
  }

  /** `hp` and `maxHp` are in half-hearts (Minecraft's hidden precision). */
  setHealth(hp: number, maxHp: number): void {
    if (hp === this.lastHealth) return;
    this.lastHealth = hp;
    const hearts = this.hearts.length;
    for (let i = 0; i < hearts; i++) {
      const heartHp = hp - i * (maxHp / hearts);
      const per = maxHp / hearts;
      const state: 0 | 0.5 | 1 = heartHp >= per ? 1 : heartHp >= per / 2 ? 0.5 : 0;
      drawHeart(this.hearts[i], state);
    }
  }

  setAir(air: number, maxAir: number, forceShow: boolean): void {
    const show = forceShow || air < maxAir;
    if (show !== this.lastShowAir) {
      this.lastShowAir = show;
      this.airEl.style.display = show && this.heartsEl.style.display !== "none" ? "flex" : "none";
      this.updateStatusTopVisibility();
    }
    if (air === this.lastAir && !forceShow) return;
    this.lastAir = air;
    const bubbles = this.bubbles.length;
    for (let i = 0; i < bubbles; i++) {
      const threshold = (i + 1) * (maxAir / bubbles);
      drawBubble(this.bubbles[i], air >= threshold - 1e-6);
    }
  }

  setDamageFlash(intensity: number): void {
    const clamped = Math.max(0, Math.min(1, intensity));
    if (Math.abs(clamped - this.lastFlash) < 0.01) return;
    this.lastFlash = clamped;
    this.flashEl.style.opacity = String(clamped * 0.55);
  }
}

function must<T extends Element>(el: T | null): T {
  if (!el) throw new Error("HUD element missing from index.html");
  return el;
}
